'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SuccessStoryCard } from '@/sanity/pages/PageSuccessStoriesOverviewTemplate';
import { containerVariants, itemVariants } from '@/hooks/useEntranceAnimation';
import { useArticleGrid } from './useArticleGrid';
import CategoriesBar from './CategoriesBar';
import SearchInput from './SearchInput';
import ArticleCard from './ArticleCard';

export default function ArticleGrid({
  articles,
  language,
}: {
  articles: SuccessStoryCard[];
  language: string;
}) {
  const {
    categories,
    visible,
    hasMore,
    activeCategory,
    search,
    tabsRef,
    isOverflowing,
    handleCategory,
    handleSearch,
    loadMore,
    scrollTabs,
  } = useArticleGrid(articles);

  return (
    <section className="w-full flex flex-col items-center pb-[10rem] pt-[3.62rem] md:pb-[14rem]">
      {/* Filter bar */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mx-auto w-[min(176.375rem,calc(100%-4rem))] md:w-[min(176.375rem,calc(100%-8rem))] flex flex-col gap-[1.75rem] lg:gap-[7rem] mb-[5.8rem] md:flex-row md:items-center md:justify-between md:gap-[4rem] md:mb-[6rem]"
      >
        <div className="order-2 md:order-1 flex-1 min-w-0">
          <CategoriesBar
            categories={categories}
            activeCategory={activeCategory}
            isOverflowing={isOverflowing}
            tabsRef={tabsRef}
            onSelect={handleCategory}
            onScrollLeft={() => scrollTabs('left')}
            onScrollRight={() => scrollTabs('right')}
          />
        </div>
        <div className="order-1 md:order-2">
          <SearchInput value={search} onChange={handleSearch} />
        </div>
      </motion.div>

      {/* Grid */}
      {visible.length > 0 ? (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto w-[min(176.375rem,calc(100%-4rem))] md:w-[min(176.375rem,calc(100%-8rem))] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 items-stretch gap-[3rem] md:gap-[4rem]"
        >
          <AnimatePresence mode="popLayout">
            {visible.map((article, i) => (
              <motion.div
                key={article._id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, y: -8, transition: { duration: 0.2 } }}
                transition={{ delay: Math.min(i * 0.08, 0.4) }}
                layout
                className=""
              >
                <ArticleCard article={article} language={language} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mx-auto w-[min(176.375rem,calc(100%-4rem))] md:w-[min(176.375rem,calc(100%-8rem))] flex items-center justify-center py-[8rem] md:py-[12rem]"
        >
          <p className="mb-0 text-[2rem] text-black/50 tracking-[-0.06rem]">
            No articles found.
          </p>
        </motion.div>
      )}

      {/* Load more */}
      <AnimatePresence>
        {hasMore && (
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.4 }}
            onClick={loadMore}
            className="lg:mt-[10rem] mt-[3rem] lg:w-fit w-[min(176.375rem,calc(100%-4rem))] bg-black text-white text-[1.75rem] font-geist font-semibold leading-[1.347] tracking-[-0.035rem] px-[3.5rem] py-[2.25rem] rounded-[0.25rem] hover:bg-black/80 transition-colors"
          >
            Load more
          </motion.button>
        )}
      </AnimatePresence>
    </section>
  );
}
